import type { Vec2 } from '../types.js';

export interface KeyboardSample {
  move: Vec2;
  dashPressed: boolean;
  pausePressed: boolean;
  callPressed: boolean;
  shopPressed: boolean;
}

const ZERO: Vec2 = { x: 0, y: 0 };

function normalizeOrZero(x: number, y: number): Vec2 {
  const len = Math.hypot(x, y);
  if (len < 1e-8) return { x: 0, y: 0 };
  return { x: x / len, y: y / len };
}

/**
 * Keyboard input adapter.
 *
 * - WASD / Arrow keys produce a normalized move direction.
 * - Space (alt: Shift) triggers a one-tick dash press.
 * - Escape triggers a one-tick pause press.
 * - Enter triggers a one-tick wave-call press (skip the intermission early).
 * - B triggers a one-tick shop press (open the intermission supply drop).
 */
export class Keyboard {
  private up = false;
  private down = false;
  private left = false;
  private right = false;
  private dashHeld = false;
  private pauseHeld = false;
  private callHeld = false;
  private shopHeld = false;
  dashPressed = false;
  pausePressed = false;
  callPressed = false;
  shopPressed = false;

  constructor(private readonly target: Window = window) {
    this.target.addEventListener('keydown', this.onKeyDown);
    this.target.addEventListener('keyup', this.onKeyUp);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    let handled = false;
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.up = true;
        handled = true;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.down = true;
        handled = true;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.left = true;
        handled = true;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.right = true;
        handled = true;
        break;
      case 'Space':
      case 'ShiftLeft':
      case 'ShiftRight':
        if (!this.dashHeld) {
          this.dashHeld = true;
          this.dashPressed = true;
        }
        handled = true;
        break;
      case 'Escape':
        if (!this.pauseHeld) {
          this.pauseHeld = true;
          this.pausePressed = true;
        }
        handled = true;
        break;
      case 'Enter':
        if (!this.callHeld) {
          this.callHeld = true;
          this.callPressed = true;
        }
        handled = true;
        break;
      case 'KeyB':
        if (!this.shopHeld) {
          this.shopHeld = true;
          this.shopPressed = true;
        }
        handled = true;
        break;
    }
    if (handled) {
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.up = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.down = false;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.left = false;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.right = false;
        break;
      case 'Space':
      case 'ShiftLeft':
      case 'ShiftRight':
        this.dashHeld = false;
        break;
      case 'Escape':
        this.pauseHeld = false;
        break;
      case 'Enter':
        this.callHeld = false;
        break;
      case 'KeyB':
        this.shopHeld = false;
        break;
    }
  };

  sample(_tick: number): KeyboardSample {
    const rawX = (this.right ? 1 : 0) - (this.left ? 1 : 0);
    const rawY = (this.up ? 1 : 0) - (this.down ? 1 : 0);
    const move = normalizeOrZero(rawX, rawY);
    const out: KeyboardSample = {
      move: move,
      dashPressed: this.dashPressed,
      pausePressed: this.pausePressed,
      callPressed: this.callPressed,
      shopPressed: this.shopPressed,
    };
    this.dashPressed = false;
    this.pausePressed = false;
    this.callPressed = false;
    this.shopPressed = false;
    return out;
  }

  /**
   * Drop queued one-tick edges. A modal that swallows keys while the sim is
   * paused (the shop's B/Escape close) calls this on the way out so the
   * queued edges don't leak into the resumed sim (B re-opening the shop,
   * Escape instantly opening the pause menu).
   */
  clearPressedEdges(): void {
    this.dashPressed = false;
    this.pausePressed = false;
    this.callPressed = false;
    this.shopPressed = false;
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
  }
}
